'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { GraduationCap } from 'lucide-react';

/**
 * Floating "Summer Institute" shortcut. For a signed-in user who's an SI staff
 * member OR a studio admin, this shows a one-tap jump to their SI staff portal
 * from anywhere in the Studio side — the teacher portal, every admin backend
 * page, etc. Hidden inside the SI module itself (the staff workspace has its own
 * "Studio Portal" link back) and on auth/login pages. Mounted once in the root
 * layout, so it appears on every page without per-page wiring.
 */

// Paths where the pill should never show (the SI module + auth/marketing entry).
function isHiddenPath(p: string | null): boolean {
  if (!p) return true;
  return (
    p.startsWith('/summer-institute') ||
    p.startsWith('/login') ||
    p.startsWith('/admin-login') ||
    p.startsWith('/auth') ||
    p.startsWith('/pair') ||
    p.startsWith('/p/') ||
    p.startsWith('/v/')
  );
}

export default function ProgramSwitcher() {
  const pathname = usePathname();
  const [dest, setDest] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (isHiddenPath(pathname)) {
      setDest(null);
      return;
    }
    // Only probe SI access for a signed-in user. The Supabase auth token lives in
    // a `sb-<ref>-auth-token` cookie, so this gate works in any browser context
    // and skips the network call entirely for anonymous visitors.
    if (!/sb-[^=;]+-auth-token/.test(document.cookie)) {
      setDest(null);
      return;
    }
    (async () => {
      try {
        const res = await fetch('/api/summer-institute/am-i-staff');
        const j = await res.json();
        if (!cancelled) setDest(j?.staff ? j.dest || '/summer-institute/staff' : null);
      } catch {
        if (!cancelled) setDest(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  if (!dest) return null;

  return (
    <Link data-component-id="src/components/ProgramSwitcher.tsx:65:4" data-component-path="src/components/ProgramSwitcher.tsx" data-component-line="65" data-component-end-line="74" data-component-file="ProgramSwitcher.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%5Bvar%3Adest%5D%22%2C%22className%22%3A%22fixed%20right-3%20top-3%20z-%5B60%5D%20inline-flex%20items-center%20gap-1.5%20rounded-full%20bg-teal-600%20px-3.5%20py-2%20text-xs%20font-semibold%20text-white%20shadow-lg%20ring-1%20ring-black%2F5%20transition-colors%20hover%3Abg-teal-700%22%7D"
      href={dest}
      title="Go to your Summer Institute staff portal"
      className="fixed right-3 top-3 z-[60] inline-flex items-center gap-1.5 rounded-full bg-teal-600 px-3.5 py-2 text-xs font-semibold text-white shadow-lg ring-1 ring-black/5 transition-colors hover:bg-teal-700"
    >
      <GraduationCap data-component-id="src/components/ProgramSwitcher.tsx:70:6" data-component-path="src/components/ProgramSwitcher.tsx" data-component-line="70" data-component-end-line="70" data-component-file="ProgramSwitcher.tsx" data-component-name="GraduationCap" data-component-content="%7B%22elementName%22%3A%22GraduationCap%22%7D" size={14} />
      <span data-component-id="src/components/ProgramSwitcher.tsx:71:6" data-component-path="src/components/ProgramSwitcher.tsx" data-component-line="71" data-component-end-line="71" data-component-file="ProgramSwitcher.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hidden%20sm%3Ainline%22%2C%22textContent%22%3A%22Summer%20Institute%22%7D" className="hidden sm:inline">Summer Institute</span>
      <span data-component-id="src/components/ProgramSwitcher.tsx:72:6" data-component-path="src/components/ProgramSwitcher.tsx" data-component-line="72" data-component-end-line="72" data-component-file="ProgramSwitcher.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22sm%3Ahidden%22%2C%22textContent%22%3A%22SI%22%7D" className="sm:hidden">SI</span>
      <span data-component-id="src/components/ProgramSwitcher.tsx:73:6" data-component-path="src/components/ProgramSwitcher.tsx" data-component-line="73" data-component-end-line="73" data-component-file="ProgramSwitcher.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22%E2%86%92%22%7D" aria-hidden>→</span>
    </Link>
  );
}
